<script setup lang="ts">
import { Property, propertyName, PropertySchema } from "~/classes/entity"
import { InterfaceSelection } from "~/classes/entity/InterfaceSelection"
import { InterfaceFile } from "~/classes/file/InterfaceFile"
import { PropertyColor, PropertySize } from "~/classes/generated/interface"
import { sendFilePropertiesRequests } from "~/classes/handlers/file/properties"

// const console = useLogger("PanelDetails", theme.colors.purple.hex)

const { corePropertyDisplay, generativePropertyDisplay, state: panelState, targetFiles } = usePanel()
const { isOffline } = useCache()
const { addEntityEventListener, removeEntityEventListener } = useEvents()
const { createAndEditFileNotesProperty } = useProperty()
const files = computed(() => panelState.value.properties)
const singleFile = computed(() => len(files.value) === 1 ? first(files.value) : undefined)
const isEditingTags = ref(false)

function anyOfProperty(propertyId: Property) {
  return any(Object.values(files.value), f => f.hasProperty(propertyId))
}
function anyIs(condition: (file: InterfaceFile) => boolean) {
  return any(Object.values(files.value), condition)
}
function anyGenMetadata() {
  return any(Object.values(files.value), f => f.hasGenerativeMetadata())
}
function propertyOfFiles(propertyId: Property) {
  return mapped(files.value, iFile => iFile.propertyOrVirtualPropertyValue(propertyId))
}
const generativePropertyDisplayExists = computed(() => filtered(generativePropertyDisplay, p => anyOfProperty(p!.propertyId)))

function onFileSelectionUpdated(
  newFiles: Record<string, InterfaceFile>,
  oldFiles?: Record<string, InterfaceFile>,
) {
  oldFiles ??= {}
  const addedFiles = Object.values(newFiles).filter(f => !oldFiles[f.fileId])

  // console.debug("Target files updated, editing property callbacks", newFiles, oldFiles)

  // we want to deselect the tag editor when the selection changes
  isEditingTags.value = false

  for (const fileId in panelState.value.properties) {
    if (!newFiles[fileId]) {
      removeEntityEventListener("fileProperties", fileId, onFileProperties)
      delete panelState.value.properties[fileId]
    }
  }
  const requests: FilePropertiesRequestDatagram[] = []
  for (const file of addedFiles) {
    addEntityEventListener("fileProperties", file.fileId, onFileProperties)
    const requestId = generateWorkerRequestId()
    requests.push({
      requestId,
      fileId: file.fileId,
      selection: InterfaceSelection.minimumPropertiesViewSelection().inner(),
      pathSelection: InterfaceSelection.minimumPathSelection().inner(),
      offline: isOffline(), // TODO: offline mode
    })
  }
  if (requests.length) {
    // console.debug("Need to request property details from cache", requests)
    sendFilePropertiesRequests(requests)
  }
}

const panelInnerWidth = computed(() => panelState.value.width - 32)

function onFileProperties(event: FileEvent) {
  // console.debug("file properties received", event)
  const file = new InterfaceFile(event)
  const fileId = event.fileId
  const existing = panelState.value.properties[fileId]
  if (!existing)
    panelState.value.properties[fileId] = file
  else
    panelState.value.properties[fileId] = file.mergeWithNewer(existing)
}

watchImmediate(targetFiles, onFileSelectionUpdated)
</script>

<template>
  <div v-if="!len(files)" />
  <interface-scrollbar
    v-else
    vertical
    class="details-view"
  >
    <panel-details-property-section>
      <div
        v-if="singleFile"
        class="flex items-center justify-center"
        :style="{
          width: px(panelInnerWidth),
          height: px(panelInnerWidth),
        }"
      >
        <file-thumbnail
          :file="singleFile"
          :container-width="panelInnerWidth"
          :container-height="panelInnerWidth"
        />
      </div>
      <div
        v-else-if="len(files) <= 4"
        class="grid grid-cols-2 gap-1"
        :style="{
          width: px(panelInnerWidth),
          height: px(panelInnerWidth),
        }"
      >
        <file-thumbnail
          v-for="file in files"
          :key="file.fileId"
          :file
          cropped
          :container-width="panelInnerWidth / 2"
          :container-height="panelInnerWidth / 2"
        />
      </div>
      <div
        v-else-if="len(files) <= 9"
        class="grid grid-cols-3 gap-1"
        :style="{
          width: px(panelInnerWidth),
          height: px(panelInnerWidth),
        }"
      >
        <file-thumbnail
          v-for="file in files"
          :key="file.fileId"
          :file
          cropped
          :container-width="panelInnerWidth / 3"
          :container-height="panelInnerWidth / 3"
        />
      </div>
      <div
        v-else
        class="grid grid-cols-4 gap-1"
        :style="{
          width: px(panelInnerWidth),
          height: px(panelInnerWidth),
        }"
      >
        <file-thumbnail
          v-for="file in Object.values(files).slice(0, 16)"
          :key="file.fileId"
          :file
          cropped
          :container-width="panelInnerWidth / 4"
          :container-height="panelInnerWidth / 4"
        />
      </div>
      <div class="property-actions-bar">
        <!-- TODO: open in / desktop -->
        <panel-button v-if="false">
          <template #icon>
            <icon-property
              size="xs"
              filled
              type="external"
            />
          </template>
          Open in...
        </panel-button>
        <panel-details-menu v-if="singleFile" :file="singleFile" />
      </div>
    </panel-details-property-section>
    <panel-details-property-section v-if="len(files) > 1">
      <template #header>
        Selection
      </template>
      <template #default>
        <panel-details-property inline label="Items">
          {{ len(files) }}
        </panel-details-property>
      </template>
    </panel-details-property-section>
    <panel-details-property-section>
      <template #header>
        Properties
      </template>
      <template #default>
        <!-- TODO: editable in corePropertyDisplay  -->
        <template
          v-for="{ monospace, multiline, inline, propertyId }, key in corePropertyDisplay"
          :key
        >
          <panel-details-property-path
            v-if="propertyId === Property.FILE_VIRTUAL_PATH"
            :files="targetFiles"
          />
          <panel-details-property-field
            v-else-if="anyOfProperty(propertyId)"
            :property-id
            :monospace
            :inline
            :multiline
            :label="propertyName(propertyId)"
            :values="propertyOfFiles(propertyId)"
          />
        </template>
      </template>
    </panel-details-property-section>
    <panel-details-property-search-indexing-status :files />
    <panel-details-property-section v-if="anyGenMetadata()">
      <template #header>
        Generation Metadata
      </template>
      <template #default>
        <!-- TODO: editable in corePropertyDisplay  -->
        <panel-details-property-field
          v-for="{ monospace, multiline, inline, propertyId }, key in generativePropertyDisplayExists"
          :key
          :property-id
          :inline
          :monospace
          :multiline
          :label="propertyName(propertyId)"
          :values="propertyOfFiles(propertyId)"
        />
      </template>
    </panel-details-property-section>
    <panel-details-property-section v-if="singleFile">
      <template #header>
        Details
      </template>
      <template #default>
        <panel-details-property
          multiline
          label="Tags"
          :property-id="Property.FILE_VIRTUAL_TAGS"
        >
          <file-property-tag-editor
            v-if="singleFile.tags().size > 0 || isEditingTags"
            :file="singleFile"
            :focus-on-mount="isEditingTags"
          />
          <div
            v-else
            class="button"
            @click="isEditingTags = true"
          >
            <icon-action
              type="plus"
              size="xs"
            />
            <div>Add Tags</div>
          </div>
        </panel-details-property>
        <panel-details-property
          multiline
          label="Notes"
          :property-id="Property.FILE_NOTES"
        >
          <panel-details-property-editable-text
            v-if="singleFile.hasProperty(Property.FILE_NOTES)"
            :file="singleFile"
            :schema-type="PropertySchema.AnyStringValue"
            :property-id="Property.FILE_NOTES"
            newlines
          />
          <div
            v-else
            class="button"
            @click="createAndEditFileNotesProperty(singleFile)"
          >
            <icon-action
              type="plus"
              size="xs"
            />
            <div>Add Notes</div>
          </div>
        </panel-details-property>
        <panel-details-property
          v-if="singleFile.hasProperty(Property.FILE_RATING)"
          multiline
          label="Rating"
          :property-id="Property.FILE_RATING"
        >
          <file-property-rating
            :file="singleFile"
            :size="PropertySize.Medium"
            :color="PropertyColor.Amber"
            editable
          />
        </panel-details-property>
      </template>
    </panel-details-property-section>
    <panel-details-property-image-metadata v-if="anyIs(f => f.isImage())" :files />
    <panel-details-property-pdf-metadata v-if="anyIs(f => f.isPdf())" :files />
    <panel-details-property-audio-metadata v-if="anyIs(f => f.isAudio())" :files />
    <panel-details-property-video-metadata v-if="anyIs(f => f.isVideo())" :files />
    <panel-details-property-link-metadata v-if="anyIs(f => f.isLink())" :files />
  </interface-scrollbar>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.details-view
  @apply select-text

.value
  @apply t-panel-sm
  &.multiline
    @apply whitespace-normal overflow-auto max-h-40 break-words
  &:not(.multiline)
    @apply text-ellipses-inline
  &.monospace
    @apply

.button
  @apply px-2 py-1 rounded-md hover:bg-accent-neutral inline-flex text-text-tertiary hover:text-[#{$global-t-default}] items-center gap-1 cursor-pointer

.property-actions-bar
  @apply flex justify-right gap-[#{$panel-properties-gap-properties}]
</style>
